/**
 * @file
 * A generic component class.
 */

import {
  isValidInstance,
  isValidType,
  isValidClassList,
  isQuerySelector,
  isValidState,
  isValidEvent,
  isValidEventType,
  hasValidRootDOMElement,
} from "./validate.js";
import StorageManager from "./StorageManager.js";
import { addClass, removeClass } from "./domHelpers.js";

class Component {
  /**
   * The DOM elements within the component.
   *
   * @protected
   *
   * @type {Object<HTMLElement, HTMLElement[]>}
   */
  _dom = {};

  /**
   * The root DOM element of the component.
   *
   * @protected
   *
   * @type {string}
   */
  _rootDOMElement = "";

  /**
   * The DOM elements within the component that cannot be reset or generated by the component itself.
   *
   * @protected
   *
   * @type {string[]}
   */
  _protectedDOMElements = [];

  /**
   * The query selectors used by the component.
   *
   * @protected
   *
   * @type {Object<string>}
   */
  _selectors = {};

  /**
   * The instantiated elements within the component.
   *
   * @protected
   *
   * @type {object}
   */
  _elements = {};

  /**
   * The CSS classes to apply when the component is in various states.
   *
   * @protected
   *
   * @type {Object<string, string[]>}
   */
  _classes = {
    initialize: "",
  };

  /**
   * The duration times (in milliseconds) for various aspects throughout the component.
   *
   * @protected
   *
   * @type {Object<number>}
   */
  _durations = {};

  /**
   * The delay times (in milliseconds) for various aspects throughout the component.
   *
   * @protected
   *
   * @type {Object<number>}
   */
  _delays = {};

  /**
   * The current state of the component's focus.
   *
   * @protected
   *
   * @type {string}
   */
  _focusState = "none";

  /**
   * The last type of event triggered within the component.
   *
   * @protected
   *
   * @type {string}
   */
  _currentEvent = "none";

  /**
   * The breakoint that the component will call media query list events.
   *
   * @protected
   *
   * @type {string}
   */
  _breakpoint = "";

  /**
   * The media query to use to trigger media query list events.
   *
   * @type {string}
   */
  _mediaQueryString = "";

  /**
   * This MediaQueryList for the component.
   *
   * @protected
   *
   * @type {MediaQueryList|null}
   */
  _mediaQueryList = null;

  /**
   * A callback for media query list events.
   *
   * @protected
   *
   * @type {Function}
   *
   * @param {MediaQueryListEvent} event - The event.
   */
  _mediaQueryListEventCallback = (event) => {
    // Add functionality to handle media matches.
    if (event.matches) {
      // Do something.
    } else {
      // Do something else.
    }
  };

  /**
   * Intervals throughout the component.
   *
   * @protected
   *
   * @type {Object<Function>}
   */
  _intervals = {};

  /**
   * Timeouts throughout the component.
   *
   * @protected
   *
   * @type {Object<Function>}
   */
  _timeouts = {};

  /**
   * Event listeners throughout the component.
   *
   * @protected
   *
   * @type {object[]}
   */
  _listeners = [];

  /**
   * Custom events that can be triggered throughout the component.
   *
   * @protected
   *
   * @type {Object<CustomEvent>}
   */
  _events = {
    initialize: new CustomEvent("grauplComponentInitialize", {
      detail: { component: this },
    }),
    preinitialize: new CustomEvent("grauplComponentPreinitialize", {
      detail: { component: this },
    }),
    postinitialize: new CustomEvent("grauplComponentPostinitialize", {
      detail: { component: this },
    }),
    validate: new CustomEvent("grauplComponentValidate", {
      detail: { component: this },
    }),
    prevalidate: new CustomEvent("grauplComponentPrevalidate", {
      detail: { component: this },
    }),
    postvalidate: new CustomEvent("grauplComponentPostvalidate", {
      detail: { component: this },
    }),
  };

  /**
   * The prefix used for CSS custom properties and attributes.
   *
   * @protected
   *
   * @type {string}
   */
  _prefix = "graupl-";

  /**
   * The key used to generate IDs throughout the component.
   *
   * @protected
   *
   * @type {string}
   */
  _key = "";

  /**
   * The key used for storage.
   *
   * @protected
   *
   * @type {string}
   */
  _storageKey = "components";

  /**
   * The main ID of the component.
   *
   * @protected
   *
   * @type {string}
   */
  _id = "";

  /**
   * The validity state of the component.
   *
   * @protected
   *
   * @type {boolean}
   */
  _valid = true;

  /**
   * The initialized state of the component.
   *
   * @protected
   *
   * @type {boolean}
   */
  _initialized = false;

  /**
   * The errors found throughout the component.
   *
   * @protected
   *
   * @type {Error[]}
   */
  _errors = [];

  /**
   * Constructs a new component.
   *
   * @param {object}             [options = {}]                           - The options for generating the component.
   * @param {?string}            [options.prefix = graupl-]               - The prefix used for CSS custom properties and attributes.
   * @param {?string}            [options.key = null]                     - The key used to generate IDs throughout the component.
   * @param {?(string|string[])} [options.initializeClass = initializing] - The class(es) to apply when the component is initializing.
   */
  constructor({
    prefix = "graupl-",
    key = null,
    initializeClass = "initializing",
  } = {}) {
    // Set the classes.
    this._classes.initialize = initializeClass || "";

    // Set the prefix and key.
    this._prefix = prefix || "";
    this._key = key || "";
  }

  /**
   * Initialize the component.
   */
  initialize() {
    try {
      if (!this._validate()) {
        throw new Error(
          `Graupl ${this.constructor.name}: Cannot initialize component. The following errors have been found:\n - ${this.errors
            .map((error) => error.message)
            .join("\n - ")}`
        );
      }

      addClass(this.initializeClass, this.rootDOMElement);

      this._dispatchEvent("preinitialize", this.rootDOMElement);

      // Generate the key.
      this._generateKey();

      // Set up the DOM.
      this._setDOMElements();
      this._setIds();
      this._setAriaAttributes();
      this._setCustomProps();

      // Set up child elements.
      this._createChildElements();

      // Handle events.
      this._handleMediaMatch();
      this._handleFocus();
      this._handleHover();
      this._handleClick();
      this._handleKeydown();
      this._handleKeyup();

      this._dispatchEvent("initialize", this.rootDOMElement);

      // Store the component.
      this._store();

      removeClass(this.initializeClass, this.rootDOMElement);

      this._initialized = true;

      this._dispatchEvent("postinitialize", this.rootDOMElement);
    } catch (error) {
      console.error(error);
    }
  }
  init() {
    this.initialize();
  }

  /**
   * The DOM elements within the component.
   *
   * @readonly
   *
   * @type {object}
   *
   * @see _dom
   */
  get dom() {
    return this._dom;
  }

  /**
   * The root DOM element of the component.
   *
   * @readonly
   *
   * @type {HTMLElement}
   *
   * @see _rootDOMElement
   */
  get rootDOMElement() {
    return this._dom[this._rootDOMElement] || document.documentElement;
  }

  /**
   * The query selectors used by the component.
   *
   * @readonly
   *
   * @type {object}
   *
   * @see _selectors
   */
  get selectors() {
    return this._selectors;
  }

  /**
   * The instantiated elements within the component.
   *
   * @readonly
   *
   * @type {object}
   *
   * @see _elements
   */
  get elements() {
    return this._elements;
  }

  /**
   * The CSS classes to apply when the component is in various states.
   *
   * @readonly
   *
   * @type {object}
   *
   * @see _classes
   */
  get classes() {
    return this._classes;
  }

  /**
   * The duration times (in milliseconds) for various aspects throughout the component.
   *
   * @readonly
   *
   * @type {object}
   *
   * @see _durations
   */
  get durations() {
    return this._durations;
  }

  /**
   * The delay times (in milliseconds) for various aspects throughout the component.
   *
   * @readonly
   *
   * @type {object}
   *
   * @see _delays
   */
  get delays() {
    return this._delays;
  }

  /**
   * Intervals throughout the component.
   *
   * @readonly
   *
   * @type {object}
   *
   * @see _intervals
   */
  get intervals() {
    return this._intervals;
  }

  /**
   * Timeouts throughout the component.
   *
   * @readonly
   *
   * @type {object}
   *
   * @see _timeouts
   */
  get timeouts() {
    return this._timeouts;
  }

  /**
   * Event listeners throughout the component.
   *
   * @readonly
   *
   * @type {object[]}
   *
   * @see _listeners
   */
  get listeners() {
    return this._listeners;
  }

  /**
   * Custom events that can be triggered throughout the component.
   *
   * @readonly
   *
   * @type {Error[]}
   *
   * @see _events
   */
  get events() {
    return this._events;
  }

  /**
   * The class(es) to apply when the component is initializing.
   *
   * @type {string|string[]}
   *
   * @see _classes.initialize
   */
  get initializeClass() {
    return this._classes.initialize;
  }

  set initializeClass(value) {
    isValidClassList({ initializeClass: value });

    if (this._classes.initialize !== value) {
      this._classes.initialize = value;
    }
  }

  /**
   * The current state of the component's focus.
   *
   * @type {string}
   *
   * @see _focusState
   */
  get focusState() {
    return this._focusState;
  }

  set focusState(value) {
    isValidState({ focusState: value });

    if (this._focusState !== value) {
      this._focusState = value;
    }
  }

  /**
   * The last type of event triggered within the component.
   *
   * @type {string}
   *
   * @see _currentEvent
   */
  get currentEvent() {
    return this._currentEvent;
  }

  set currentEvent(value) {
    isValidEvent({ currentEvent: value });

    if (this._currentEvent !== value) {
      this._currentEvent = value;
    }
  }

  /**
   * The breakoint that the component will call media query list events.
   *
   * @type {string}
   *
   * @see _breakpoint
   */
  get breakpoint() {
    return this._breakpoint;
  }

  set breakpoint(value) {
    isValidType("string", { breakpoint: value });

    if (this._breakpoint !== value) {
      this._breakpoint = value;
    }
  }

  /**
   * The media query to use to trigger media query list events.
   *
   * @type {string}
   *
   * @see _mediaQueryString
   */
  get mediaQuery() {
    if (this._mediaQueryString !== "") {
      return this._mediaQueryString;
    }

    if (this._breakpoint === "") {
      return "";
    }

    return `(width <= ${this._breakpoint})`;
  }

  set mediaQuery(value) {
    isValidType("string", { mediaQuery: value });

    if (this._mediaQueryString !== value) {
      this._mediaQueryString = value;
    }
  }

  /**
   * The prefix used for CSS custom properties and attributes.
   *
   * @readonly
   *
   * @type {string}
   *
   * @see _prefix
   */
  get prefix() {
    return this._prefix;
  }

  /**
   * The key used to generate IDs throughout the component.
   *
   * @readonly
   *
   * @type {string}
   *
   * @see _key
   */
  get key() {
    return this._key;
  }

  /**
   * The main ID of the component.
   *
   * @readonly
   *
   * @type {string}
   *
   * @see _id
   */
  get id() {
    return this._id;
  }

  /**
   * The validity state of the component.
   *
   * @readonly
   *
   * @type {boolean}
   *
   * @see _valid
   */
  get isValid() {
    return this._valid;
  }

  /**
   * The initialized state of the component.
   *
   * @readonly
   *
   * @type {boolean}
   *
   * @see _initialized
   */
  get isInitialized() {
    return this._initialized;
  }

  /**
   * An array to hold error messages.
   *
   * @readonly
   *
   * @type {string[]}
   *
   * @see _errors
   */
  get errors() {
    return this._errors;
  }

  /**
   * Validates all aspects of the component to ensure proper functionality.
   *
   * Keys are altered to match the arguments passed in during creation where possible.
   *
   * @protected
   *
   * @return {boolean} - The result of the validation checks.
   */
  _validate() {
    this._dispatchEvent("prevalidate", this.rootDOMElement);

    // _rootDOMElement check.
    const rootDOMElementCheck = hasValidRootDOMElement(this, {
      shouldThrow: false,
    });

    // Handle _rootDOMElement check failure.
    if (!rootDOMElementCheck.status) {
      this._errors = [...this._errors, ...rootDOMElementCheck.errors];
      this._valid = false;
    }

    // DOM checks.
    if (Object.keys(this._dom).length > 0) {
      const domElements = {};

      // Loop through and add "Element" to the end of each key in _dom.
      for (const domKey of Object.keys(this._dom)) {
        // If we're dealing with an array, we need to check each element in the array.
        if (Array.isArray(this._dom[domKey])) {
          this._dom[domKey].forEach((element, index) => {
            domElements[`${domKey}Element[${index}]`] = element;
          });
        } else if (this._dom[domKey] !== null) {
          domElements[`${domKey}Element`] = this._dom[domKey];
        }
      }

      // Check the DOM elements.
      const domChecks = isValidInstance(HTMLElement, domElements, {
        shouldThrow: false,
      });

      // Handle DOM check failure.
      if (!domChecks.status) {
        this._errors = [...this._errors, ...domChecks.errors];
        this._valid = false;
      }
    }

    // Query selector checks.
    if (Object.keys(this._selectors).length > 0) {
      const querySelectors = {};

      // Loop through and add "Selector" to the end of each key in _selectors.
      for (const querySelector of Object.keys(this._selectors)) {
        querySelectors[`${querySelector}Selector`] =
          this._selectors[querySelector];
      }

      // Check the query selectors.
      const querySelectorChecks = isQuerySelector(querySelectors, {
        shouldThrow: false,
      });

      // Handle query selector check failure.
      if (!querySelectorChecks.status) {
        this._errors = [...this._errors, ...querySelectorChecks.errors];
        this._valid = false;
      }
    }

    // Class list checks.
    if (Object.keys(this._classes).length > 0) {
      const classes = {};

      // Loop through and add "Class" to the end of each key in _classes.
      for (const className of Object.keys(this._classes)) {
        if (this._classes[className] === "") {
          continue;
        }

        classes[`${className}Class`] = this._classes[className];
      }

      // Check the class lists.
      const classListChecks = isValidClassList(classes, { shouldThrow: false });

      // Handle class list check failure.
      if (!classListChecks.status) {
        this._errors = [...this._errors, ...classListChecks.errors];
        this._valid = false;
      }
    }

    // Duration checks.
    if (Object.keys(this._durations).length > 0) {
      const durations = {};

      // Loop through and add "Duration" to the end of each key in _durations.
      for (const durationName of Object.keys(this._durations)) {
        durations[`${durationName}Duration`] = this._durations[durationName];
      }

      // Check the durations.
      const durationChecks = isValidType("number", durations, {
        shouldThrow: false,
      });

      // Handle duration check failure.
      if (!durationChecks.status) {
        this._errors = [...this._errors, ...durationChecks.errors];
        this._valid = false;
      }
    }

    // Delay checks.
    if (Object.keys(this.delays).length > 0) {
      const delays = {};

      // Loop through and add "Delay" to the end of each key in delays.
      for (const delayName of Object.keys(this.delays)) {
        delays[`${delayName}Delay`] = this.delays[delayName];
      }

      // Check the delays.
      const delayChecks = isValidType("number", delays, { shouldThrow: false });

      // Handle delay check failure.
      if (!delayChecks.status) {
        this._errors = [...this._errors, ...delayChecks.errors];
        this._valid = false;
      }
    }

    // String checks.
    const strings = {
      _storageKey: this._storageKey,
      key: this._key,
      prefix: this._prefix,
      mediaQuery: this._mediaQueryString,
      breakpoint: this._breakpoint,
    };

    this._protectedDOMElements.forEach((elementType) => {
      strings[`_protectedDOMElementType[${elementType}]`] = elementType;
    });
    // Check the strings.
    const stringChecks = isValidType("string", strings, { shouldThrow: false });

    // Handle string check failure.
    if (!stringChecks.status) {
      this._errors = [...this._errors, ...stringChecks.errors];
      this._valid = false;
    }

    this._dispatchEvent("validate", this.rootDOMElement);

    this._dispatchEvent("postvalidate", this.rootDOMElement);

    return this._valid;
  }

  /**
   * Generates a key for the component.
   *
   * @param {boolean} [regenerate = false] - A flag to determine if the key should be regenerated.
   */
  _generateKey(regenerate = false) {
    if (this._key === "" || regenerate) {
      this._key = Math.random()
        .toString(36)
        .replace(/[^a-z]+/g, "")
        .substring(0, 10);
    }
  }

  /**
   * Sets IDs throughout the component.
   */
  _setIds() {
    // Add functionality to set IDs throughout the component.
  }

  /**
   * Sets ARIA attributes throughout the component.
   */
  _setAriaAttributes() {
    // Add functionality to set attributes throughout the component.
  }

  /**
   * Sets custom props throughout the component.
   */
  _setCustomProps() {
    // Add functionality to set custom props throughout the component.
  }

  /**
   * Sets DOM elements throughout the component.
   *
   * Elements listed in _protectedDOMElements cannot be set using this method.
   *
   * @protected
   *
   * @param {string}                      elementType                - The type of element to populate.
   * @param {Object<HTMLElement,boolean>} [options = {}]             - The options for setting the DOM element type.
   * @param {HTMLElement}                 [options.context]          - The element used as the base context for the querySelector.
   * @param {boolean}                     [options.overwrite = true] - A flag to set if the existing elements will be overwritten.
   * @param {boolean}                     [options.strict = false]   - A flag to set if the elements must be direct children of the base.
   */
  _setDOMElementType(
    elementType,
    { context, overwrite = true, strict = false } = {}
  ) {
    // Make sure the element type is valid.
    if (typeof this.selectors[elementType] !== "string") {
      throw new Error(
        `Graupl ${this.constructor.name}: "${elementType}" is not a valid element type.`
      );
    }

    // Make sure the element type can actually be set through this method.
    if (
      this._rootDOMElement === elementType ||
      this._protectedDOMElements.includes(elementType)
    ) {
      throw new Error(
        `Graupl ${this.constructor.name}: "${elementType}" element cannot be set through _setDOMElementType because it is a protected element.`
      );
    }

    // Make sure the context element is actually an HTMLELement.
    isValidInstance(HTMLElement, { context });

    // Get the all elements matching the selector in the context.
    const domElements = Array.from(
      context.querySelectorAll(this.selectors[elementType])
    );

    // Filter the elements so if `strict` is true, only direct children of the context are kept.
    const filteredElements = domElements.filter((item) =>
      strict ? item.parentElement === context : true
    );

    if (Array.isArray(this._dom[elementType])) {
      if (overwrite) {
        this._dom[elementType] = filteredElements;
      } else {
        this._dom[elementType] = [
          ...this._dom[elementType],
          ...filteredElements,
        ];
      }
    } else {
      this._dom[elementType] = filteredElements[0] || null;
    }
  }

  /**
   * Resets DOM elements throughout the component.
   *
   * Elements listed in _protectedDOMElements cannot be reset using this method.
   *
   * @protected
   *
   * @param {string} elementType - The type of element to clear.
   */
  _resetDOMElementType(elementType) {
    // Make sure the element type is valid.
    if (typeof this.selectors[elementType] !== "string") {
      throw new Error(
        `Graupl ${this.constructor.name}: "${elementType}" is not a valid element type.`
      );
    }

    // Make sure the element type can actually be reset through this method.
    if (
      this._rootDOMElement === elementType ||
      this._protectedDOMElements.includes(elementType)
    ) {
      throw new Error(
        `Graupl ${this.constructor.name}: "${elementType}" element cannot be reset through _resetDOMElementType because it is a protected element.`
      );
    }

    if (Array.isArray(this._dom[elementType])) {
      this._dom[elementType] = [];
    } else {
      this._dom[elementType] = null;
    }
  }

  /**
   * Sets all DOM elements throughout the component.
   *
   * Utilizes _setDOMElementType and _resetDOMElementType.
   *
   * @protected
   */
  _setDOMElements() {
    // Add functionality to set DOM Elements throughout the component.
  }

  /**
   * Creates and initializes child elements throughout the component.
   *
   * @protected
   */
  _createChildElements() {
    // Add functionality to handle creating child elements throughout the component.
  }

  /**
   * Handles media match events throughout the component.
   *
   * @protected
   */
  _handleMediaMatch() {
    if (this.mediaQuery === "") {
      return;
    }

    this._mediaQueryList = window.matchMedia(this.mediaQuery);
    this._addEventListener(
      "change",
      this._mediaQueryList,
      this._mediaQueryListEventCallback
    );
    this._mediaQueryListEventCallback(this._mediaQueryList);
  }

  /**
   * Handles focus events through the component.
   *
   * @protected
   */
  _handleFocus() {
    // Add functionality to handle focus events throughout the component.
  }

  /**
   * Handles click events through the component.
   *
   * @protected
   */
  _handleClick() {
    // Add functionality to handle click events throughout the component.
  }

  /**
   * Handles hover events through the component.
   *
   * @protected
   */
  _handleHover() {
    // Add functionality to handle hover events throughout the component.
  }

  /**
   * Handles keydown events through the component.
   *
   * @protected
   */
  _handleKeydown() {
    // Add functionality to handle keydown events throughout the component.
  }

  /**
   * Handles keyup events through the component.
   *
   * @protected
   */
  _handleKeyup() {
    // Add functionality to handle keyup events throughout the component.
  }

  /**
   * Stores the component into the global Graupl storage object.
   *
   * @protected
   */
  _store() {
    // Set up the storage.
    if (
      !isValidInstance(
        StorageManager,
        { storage: window.GrauplStorage },
        { shouldThrow: false }
      ).status
    ) {
      new StorageManager({ scope: "GrauplStorage" });
    }

    // Store the menu
    window.GrauplStorage.set({
      key: this.id !== "" ? this.id : this.key,
      type: this._storageKey,
      data: this,
    });
  }

  /**
   * Removes the component from the global Graupl storage object.
   *
   * @protected
   */
  _unstore() {
    if (
      !isValidInstance(
        StorageManager,
        { storage: window.GrauplStorage },
        { shouldThrow: false }
      ).status
    ) {
      return;
    }

    window.GrauplStorage.clear({
      key: this.id !== "" ? this.id : this.key,
      type: this._storageKey,
    });
  }

  /**
   * Sets an interval within the component.
   *
   * @protected
   *
   * @param {Function} [callback]         - The callback function.
   * @param {number}   [delay]            - The time (in milliseconds) of the delay.
   * @param {string}   [scope = _default] - The scope of the interval (used to store the interval in _intervals).
   */
  _setInterval(callback, delay, scope = "_default") {
    this._clearInterval(scope);

    this._intervals[scope] = setInterval(callback, delay);
  }

  /**
   * Clears an interval within the component.
   *
   * @protected
   *
   * @param {string} [scope = _default] - The scope of the interval (used to get the interval from _intervals).
   */
  _clearInterval(scope = "_default") {
    clearInterval(this._intervals[scope]);
  }

  /**
   * Clears all intervals within the component.
   *
   * @protected
   */
  _clearIntervals() {
    for (const scope of Object.keys(this._intervals)) {
      this._clearInterval(scope);
    }
  }

  /**
   * Sets a timeout within the component.
   *
   * @protected
   *
   * @param {Function} [callback]         - The callback function.
   * @param {number}   [delay]            - The time (in milliseconds) of the delay.
   * @param {string}   [scope = _default] - The scope of the timeout (used to store the timeout in _timeouts).
   */
  _setTimeout(callback, delay, scope = "_default") {
    this._clearTimeout(scope);

    this._timeouts[scope] = setTimeout(callback, delay);
  }

  /**
   * Clears a timeout within the component.
   *
   * @protected
   *
   * @param {string} [scope = _default] - The scope of the timeout (used to get the timeout from _timeouts).
   */
  _clearTimeout(scope = "_default") {
    clearTimeout(this._timeouts[scope]);
  }

  /**
   * Clears all timeouts within the component.
   *
   * @protected
   */
  _clearTimeouts() {
    for (const scope of Object.keys(this._timeouts)) {
      this._clearTimeout(scope);
    }
  }

  /**
   * Registers a new event type within the component.
   *
   * @protected
   *
   * @param {string}  name                     - The name of the new event type.
   * @param {object}  [options = {}]           - The options for the new event type.
   * @param {boolean} [options.bubbles = true] - A flag to set if the event bubbles.
   * @param {object}  [options.detail = {}]    - Additional details to include in the event.
   */
  _registerEvent(name, { bubbles = true, detail = {} } = {}) {
    isValidType("string", { name });
    isValidType("boolean", { bubbles });
    isValidType("object", { detail });

    const eventName = `graupl${this.constructor.name}${name.charAt(0).toUpperCase()}${name.slice(
      1
    )}`;

    this._events[name] = new CustomEvent(eventName, {
      bubbles,
      detail: { component: this, ...detail },
    });
  }

  /**
   * Dispatch a custom event on an element in the DOM.
   *
   * @param {string}      eventType - The type of the event to dispatch.
   * @param {HTMLElement} element   - The element to dispatch the event on.
   */
  _dispatchEvent(eventType, element) {
    // Make sure the event type exists.
    isValidEventType(eventType, this);

    // Make sure the element is actually an HTML Element.
    isValidInstance(HTMLElement, { element });

    // Dispatch the event.
    element.dispatchEvent(this.events[eventType]);
  }

  /**
   * Add an event listener to an element and register it within the component.
   *
   * @param {string}         type           - The type of event to listen for.
   * @param {HTMLElement}    element        - The element to add the listener to.
   * @param {Function}       listener       - The listener callback.
   * @param {object|boolean} [options = {}] - Options to pass to the listener.
   */
  _addEventListener(type, element, listener, options = {}) {
    // Add the listener.
    element.addEventListener(type, listener, options);

    // Store it in the component.
    this._listeners.push({
      type,
      element,
      listener,
      options,
    });
  }

  /**
   * Remove an event listener from an element and unregister it within the component.
   *
   * @param {string}         type           - The type of event to remove.
   * @param {HTMLElement}    element        - The element to remove the listener from.
   * @param {Function}       listener       - The listener callback.
   * @param {object|boolean} [options = {}] - Options to pass to the listener.
   */
  _removeEventListener(type, element, listener, options = {}) {
    // Remove the listener.
    element.removeEventListener(type, listener, options);

    // Find the listener in the component's listener storage.
    let index = -1;

    this._listeners.forEach((registeredListener, i) => {
      if (
        registeredListener.type === type &&
        registeredListener.element === element &&
        registeredListener.listener === listener &&
        JSON.stringify(registeredListener.options) === JSON.stringify(options)
      ) {
        index = i;
      }
    });

    // Remove it from the component's listener storage.
    if (index !== -1) {
      this._listeners.splice(index, 1);
    }
  }

  /**
   * Removes all event listeners registered in the component.
   *
   * This can be filtered by type and/or element.
   *
   * @protected
   *
   * @param {object}       [options = {}]           - Options for removing listeners.
   * @param {?string}      [options.type = null]    - The type of event to remove. If null, all types are removed.
   * @param {?HTMLElement} [options.element = null] - The element to remove listeners from. If null, all elements are removed.
   */
  _removeEventListeners({ type = null, element = null } = {}) {
    const listeners = [...this._listeners];

    listeners.forEach((listener) => {
      if (type !== null && listener.type !== type) return;
      if (element !== null && listener.element !== element) return;

      this._removeEventListener(
        listener.type,
        listener.element,
        listener.listener,
        listener.options
      );
    });
  }

  /**
   * Disposes of the current instantiated component.
   *
   * Removes all timeouts and event listeners, removes the component from the global storage, and delete's the object.
   */
  dispose() {
    this._clearIntervals();
    this._clearTimeouts();
    this._removeEventListeners();
    this._unstore();

    delete this;
  }
}

export default Component;
